import { Instance, types } from "mobx-state-tree";
import { SnoozedStatus } from "../base/Conversation";
import { IInboxEntry } from "./InboxEntry";
import { IInboxFilter, InboxFilterId } from "./InboxFilter";
import { TagGroup } from "../tagGroups/TagGroupStore";

interface FilterViews {
  doesEntryBelong(entry: IInboxEntry, currentFilter: IInboxFilter): boolean;
}
export enum GlobalFilterTypes {
  Tags = "Tags",
  TagGroup = "TagGroup",
  Snoozed = "Snoozed",
  Assignee = "Assignee",
  AnyThreadClosedAt = "AnyThreadClosedAt",
  YoursUnsnoozed = "YoursUnsnoozed",
  YoursNew = "YoursNew"
}

export const FilterTags = types
  .model("FilterTags", {
    tags: types.array(types.string),
    type: GlobalFilterTypes.Tags
  })
  .views(
    (self): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry) {
        return (
          entry.tags.length > 0 && !!self.tags.find((filterTag) => entry.tags.includes(filterTag))
        );
      }
    })
  );

export interface IFilterTags extends Instance<typeof FilterTags> {}

export const FilterTagGroup = types
  .model("FilterTagGroup", {
    tagGroups: types.array(TagGroup),
    type: GlobalFilterTypes.TagGroup
  })
  .views(
    (self): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry) {
        return Boolean(
          entry.owner.tagGroupId &&
            self.tagGroups.find((filterTagGroup) => entry.owner.tagGroupId === filterTagGroup.id)
        );
      }
    })
  );

export interface IFilterTagGroup extends Instance<typeof FilterTagGroup> {}

export const FilterSnoozed = types
  .model("FilterSnoozed", {
    type: GlobalFilterTypes.Snoozed
  })
  .views(
    (): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry) {
        return entry.isSnoozed;
      }
    })
  );

export const FilterAssignee = types
  .model("FilterAssignee", {
    type: GlobalFilterTypes.Assignee,
    assigneeId: types.string
  })
  .views(
    (self): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry) {
        return !!entry.assignee && entry.assignee.id === self.assigneeId;
      }
    })
  );
export interface IFilterAssignee extends Instance<typeof FilterAssignee> {}

export const FilterYoursUnsnoozed = types
  .model("FilterYoursUnsnoozed", {
    type: GlobalFilterTypes.YoursUnsnoozed
  })
  .views(
    (): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry, filter: IInboxFilter) {
        return (
          filter.id !== InboxFilterId.YOURS ||
          (!!entry.snoozedStatus && entry.snoozedStatus === SnoozedStatus.Unsnoozed)
        );
      }
    })
  );
export interface IFilterYoursUnsnoozed extends Instance<typeof FilterYoursUnsnoozed> {}

export const FilterYoursNew = types
  .model("FilterYoursNew", {
    type: GlobalFilterTypes.YoursNew
  })
  .views(
    (self): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry, filter: IInboxFilter) {
        return filter.id !== InboxFilterId.YOURS || !entry.snoozedStatus;
      }
    })
  );
export interface IFilterYoursNew extends Instance<typeof FilterYoursNew> {}

export const FilterAnyThreadClosed = types
  .model("FilterAnyThreadClosed", {
    type: GlobalFilterTypes.AnyThreadClosedAt,
    after: types.number,
    before: types.number
  })
  .views(
    (self): FilterViews => ({
      doesEntryBelong(entry: IInboxEntry) {
        return true;
      }
    })
  );
export interface IFilterAnyThreadClosed extends Instance<typeof FilterAnyThreadClosed> {}

export const ConcreteGlobalFilters = types.union(
  FilterTags,
  FilterTagGroup,
  FilterSnoozed,
  FilterAssignee,
  FilterAnyThreadClosed,
  FilterYoursUnsnoozed,
  FilterYoursNew
);

/**
 * Create filter instance of given type, provided it does not need any parameters
 */
export const createGlobalFilterOfType = (
  type: Exclude<GlobalFilterTypes, GlobalFilterTypes.Tags>
) => {
  switch (type) {
    case GlobalFilterTypes.Snoozed:
      return FilterSnoozed.create();
    case GlobalFilterTypes.YoursNew:
      return FilterYoursNew.create();
    case GlobalFilterTypes.YoursUnsnoozed:
      return FilterYoursUnsnoozed.create();
    default:
      throw Error("Unkown filter type");
  }
};
